import ParameterValidator from '@mvps-genesis/parameter-validator';

/*
 *--------------------------------------------------------------------------
 * ProductStickyHeader
 *--------------------------------------------------------------------------
 *
 * Product sticky header functionality
 *
 */

export default class ProductStickyHeader {

	/**
	 * Parameter Validator object
	 *
	 * @var	@mvps-genesis/parameter-validator
	 */
	#validator;

	/**
	 * The root element
	 *
	 * @var HTMLElement
	 */
	rootElement;

	/**
	 * The top-level container element
	 *
	 * @var HTMLElement
	 */
	containerElement;

	/**
	 * The CTA element to trigger the add to cart action
	 *
	 * @var HTMLElement
	 */
	atcElement;

	/**
	 * Flag to determine if the sticky/unsticky functionality should wait for css animations to finish. This should be set to true if css animations are being used.
	 *
	 * @var Boolean
	 */
	awaitAnimations;

	/**
	 * The product image element
	 *
	 * @var HTMLElement
	 */
	imageElement;

	/**
	 * The product price value container element
	 *
	 * @var HTMLElement
	 */
	priceValueElement;

	/**
	 * The element to target for triggering the sticky functionality
	 *
	 * @var HTMLElement
	 */
	stickyTargetElement;

	/**
	 * The target add to cart element
	 *
	 * @var HTMLElement
	 */
	targetAtcElement;

	/**
	 * The target image element's container element
	 *
	 * @var HTMLElement
	 */
	targetImageElementContainer;

	/**
	 * The selector for the target image element
	 *
	 * @var String
	 */
	targetImageElementSelector;

	/**
	 * The target product price value container element
	 *
	 * @var HTMLElement
	 */
	targetPriceValueElement;

	/**
	 * Create a new ProductStickyHeader object
	 *
	 * @param		Element		rootElement							The root element of the sticky content.
	 * @param		Object		options								Class configuration options object.
	 * @property	Element		options.atcElement					The CTA element to trigger the add to cart action. Defaults to '#js-product-header-cta'.
	 * @property	Boolean		options.awaitAnimations				Flag to determine if the sticky/unsticky functionality should wait for css animations to finish. This should be set to true if css animations are being used. Defaults to true.
	 * @property	Element		options.containerElement			The top-level container element, typically the first child of the rootElement. Defaults to '.g-product-header__container'.
	 * @property	String		options.hiddenClass					Class to apply for showing/hiding the rootElement. Defaults to 'u-hidden'.
	 * @property	Element		options.imageElement				The product image element. Defaults to '#js-product-header-image'.
	 * @property	Element		options.priceValueElement			The product price value container element. Defaults to '#js-product-header-price'.
	 * @property	Element		options.stickyTargetElement			The element to target for triggering the sticky functionality. Defaults to '.g-product-layout-details'.
	 * @property	Element		options.targetAddToCartElement		The target add to cart element. Defaults to '.g-product-layout .js-add-to-cart'.
	 * @property	String		options.targetImageElementSelector	The selector for the target image element. Defaults to '.g-carousel__slide.is-active img'.
	 * @property	Element		options.targetImageElementContainer	The target image element's container element. Defaults to '#js-product-layout-image-slider'.
	 * @property	Element		options.targetPriceValueElement		The target product price value container element. Defaults to '.g-product-layout .g-product-layout-details__pricing'.
	 */
	// eslint-disable-next-line max-lines-per-function
	constructor (
		rootElement,
		{
			atcElement = '#js-product-header-cta',
			awaitAnimations = true,
			containerElement = '.g-product-header__container',
			hiddenClass = 'u-hidden',
			imageElement = '#js-product-header-image',
			priceValueElement = '#js-product-header-price',
			stickyTargetElement = '.g-product-layout-details',
			targetAddToCartElement = '.g-product-layout .js-add-to-cart',
			targetImageElementSelector = '.g-carousel__slide.is-active img',
			targetImageElementContainer = '#js-product-layout-image-slider',
			targetPriceValueElement = '.g-product-layout .g-product-layout-details__pricing'
		} = {}
	) {
		this.#validator = new ParameterValidator(this);

		this.rootElement = this.#validator.isElement('rootElement', rootElement);
		this.containerElement = this.#validator.isElement('containerElement', containerElement, {container: this.rootElement});
		this.awaitAnimations = window.matchMedia('(prefers-reduced-motion: no-preference)').matches ? this.#validator.isBoolean('awaitAnimations', awaitAnimations) : false;
		this.hiddenClass = this.#validator.isString('hiddenClass', hiddenClass);

		this.atcElement = this.#validator.isElement('atcElement', atcElement, {
			container: this.containerElement,
			required: false
		});
		this.targetAtcElement = this.atcElement ? this.#validator.isElement('targetAddToCartElement', targetAddToCartElement) : null;

		this.imageElement = this.#validator.isElement('imageElement', imageElement, {container: this.containerElement});
		this.targetImageElementContainer = this.#validator.isElement('targetImageElementContainer', targetImageElementContainer);
		this.targetImageElementSelector = this.#validator.isString('targetImageElementSelector', targetImageElementSelector);

		this.priceValueElement = this.#validator.isElement('priceValueElement', priceValueElement, {container: this.containerElement});
		this.targetPriceValueElement = this.#validator.isElement('targetPriceValueElement', targetPriceValueElement);

		this.setImageElementContent();
		this.observeImageElement();

		this.setPriceValueElementContent();
		this.observePriceValue();

		if (this.targetAtcElement) {
			this.setAtcElementContent();
			this.observeAddToCartElement();
		}

		this.stickyTargetElement = this.#validator.isElement('stickyTargetElement', stickyTargetElement);

		this.observeStickyTargetElement();
	}

	setImageElementContent () {
		const targetImageElement = this.targetImageElementContainer.querySelector(this.targetImageElementSelector);

		if (!targetImageElement || !targetImageElement.src || targetImageElement.src === this.imageElement.src) {
			return;
		}

		this.imageElement.src = targetImageElement.src;
	}

	setPriceValueElementContent () {
		let targetPriceValueElements = Array.from(this.targetPriceValueElement.children);
		let targetPriceValueElementClones = [];

		targetPriceValueElements.forEach((element) => {
			let elementClone = element.cloneNode(true);

			elementClone.getAttributeNames().forEach((attributeName) => {
				if (attributeName !== 'class') {
					elementClone.removeAttribute(attributeName);
				}
			});

			targetPriceValueElementClones.push(elementClone);
		});

		this.priceValueElement.replaceChildren(...targetPriceValueElementClones);
	}

	setAtcElementContent () {
		let targetAtcElementClone = this.targetAtcElement.cloneNode(true);

		targetAtcElementClone.type = 'button';

		Object.keys(targetAtcElementClone.dataset).forEach((key) => {
			delete targetAtcElementClone.dataset[key];
		});

		targetAtcElementClone.addEventListener('click', (event) => {
			event.preventDefault();

			this.targetAtcElement.click();
		});

		this.atcElement.replaceChildren(targetAtcElementClone);
	}

	observeImageElement () {
		const observer = new MutationObserver(() => {
			this.setImageElementContent();
		});

		observer.observe(this.targetImageElementContainer, {
			attributeFilter: ['tabindex'],
			subtree: true
		});
	}

	observePriceValue () {
		const observer = new MutationObserver(() => {
			this.setPriceValueElementContent();
		});

		observer.observe(this.targetPriceValueElement, {
			childList: true,
			subtree: true
		});
	}

	observeAddToCartElement () {
		const observer = new MutationObserver(() => {
			this.setAtcElementContent();
		});

		observer.observe(this.targetAtcElement, {
			attributeOldValue: true,
			childList: true,
			subtree: true
		});
	}

	observeStickyTargetElement () {
		const observer = new IntersectionObserver(([entry]) => {
			if (entry.intersectionRatio > 0) {
				this.unsticky();
			} else {
				this.sticky();
			}
		}, {
			threshold: [0]
		});

		observer.observe(this.stickyTargetElement);
	}

	sticky () {
		if (!this.rootElement.classList.contains(this.hiddenClass)) {
			return;
		}

		this.rootElement.classList.remove(this.hiddenClass);
		this.rootElement.setAttribute('aria-hidden', 'false');
	}

	unsticky () {
		if (this.rootElement.classList.contains(this.hiddenClass)) {
			return;
		}

		this.rootElement.setAttribute('aria-hidden', 'true');

		if (this.awaitAnimations) {
			const animationHandler = () => {
				this.rootElement.classList.add(this.hiddenClass);
				this.rootElement.removeEventListener('animationend', animationHandler, false);
			};

			this.rootElement.addEventListener('animationend', animationHandler, false);
		} else {
			this.rootElement.classList.add(this.hiddenClass);
		}
	}
}
